import { ReactNode, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { gsap } from "../lib/anim";
import { Icon, IconName } from "./Icons";
import { Avatar } from "./ui";
import { SyncBadge } from "./SyncBadge";
import { CommandPalette } from "./CommandPalette";
import { STUDIO, useStore, outstanding } from "../lib/store";
import { useSession, logout, ROLE_LABEL, can as canSee } from "../lib/auth";
import { SECTION_CAP } from "../lib/permissions";

export const SECTIONS: { key: string; label: string; icon: IconName }[] = [
  { key: "dashboard", label: "Dashboard", icon: "dashboard" },
  { key: "data", label: "Project data", icon: "folder" },
  { key: "brief", label: "Design Brief", icon: "brief" },
  { key: "concept", label: "Concepts", icon: "concept" },
  { key: "moodboard", label: "Mood board", icon: "palette" },
  { key: "presentation", label: "Presentation", icon: "present" },
  { key: "review", label: "Drawing review", icon: "image" },
  { key: "drawings", label: "Construction drawings", icon: "layers" },
  { key: "tendering", label: "Tendering", icon: "sign" },
  { key: "rfis", label: "RFIs", icon: "chat" },
  { key: "site", label: "Site progress", icon: "image" },
  { key: "snags", label: "Snag list", icon: "check" },
  { key: "costplan", label: "Cost plan", icon: "money" },
  { key: "stages", label: "Stages", icon: "stages" },
  { key: "schedule", label: "Schedule", icon: "calendar" },
  { key: "program", label: "Work Program", icon: "program" },
  { key: "reports", label: "Reports", icon: "report" },
  { key: "costbrief", label: "Construction budget", icon: "money" },
  { key: "estimator", label: "Fee estimator", icon: "sliders" },
  { key: "quotes", label: "Quotations", icon: "money" },
  { key: "billing", label: "Billing", icon: "money" },
  { key: "ledger", label: "Payments ledger", icon: "check" },
  { key: "records", label: "Communication log", icon: "report" },
  { key: "engagement", label: "Engagement", icon: "sign" },
  { key: "feedback", label: "Feedback", icon: "chat" },
  { key: "team", label: "Team", icon: "spark" },
  { key: "audit", label: "Audit trail", icon: "report" },
  { key: "rates", label: "Rates & fees", icon: "gear" },
];

/* --- theme ----------------------------------------------------------- */
export function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState(() => localStorage.getItem("atelier.theme") || "light");
  useEffect(() => { document.documentElement.setAttribute("data-theme", theme); localStorage.setItem("atelier.theme", theme); }, [theme]);
  return [theme, () => setTheme((t) => (t === "light" ? "dark" : "light"))];
}

export function Shell({ children }: { children: ReactNode }) {
  const { pid = "", section = "dashboard" } = useParams();
  const nav = useNavigate();
  const { projects, roleMatrix } = useStore();
  const project = projects.find((p) => p.id === pid) ?? projects[0];
  const [theme, toggleTheme] = useTheme();
  const [switcher, setSwitcher] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const session = useSession();
  const asideRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => {
      gsap.from(asideRef.current, { xPercent: -100, duration: 0.55, ease: "power3.out", clearProps: "transform" });
      gsap.from(".shell-main", { opacity: 0, x: 14, duration: 0.6, ease: "power2.out", delay: 0.12, clearProps: "all" });
    });
    return () => ctx.revert();
  }, []);
  const who = session?.name ?? STUDIO.principal;
  const roleLabel = session ? (session.role === "studio" ? "Admin" : ROLE_LABEL[session.teamRole ?? "designer"]) : "Admin";

  const openFeedback = project ? project.feedback.filter((f) => !f.resolved).length : 0;
  const owed = project ? outstanding(project) : 0;
  const allowed = SECTIONS.filter((s) => { const cap = SECTION_CAP[s.key]; return !cap || canSee(session, cap, roleMatrix); });
  const current = SECTIONS.find((s) => s.key === section);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "var(--nav-w) 1fr", minHeight: "100vh" }}>
      {/* ---------------- phone top bar + section sheet ----------------
          The sidebar is hidden on a phone; without this there was no way to
          reach any section at all — the accountant could sign in and go nowhere. */}
      <div className="m-bar">
        <button onClick={() => nav("/studio/projects")} className="row" style={{ gap: 9, background: "none", border: "none", cursor: "pointer", padding: 0, minWidth: 0 }}>
          <span style={{ width: 30, height: 30, borderRadius: 9, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center", flexShrink: 0 }}><Icon.logo width={17} /></span>
          <span style={{ minWidth: 0, textAlign: "left" }}>
            <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{project?.name}</span>
            <span className="dim" style={{ display: "block", fontSize: 10.5 }}>{current?.label ?? "Studio Cockpit"} · {roleLabel}</span>
          </span>
        </button>
        <button className="btn soft sm" onClick={() => setMenuOpen(true)} style={{ flexShrink: 0 }}><Icon.grip width={14} />Sections</button>
      </div>
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 95, background: "rgba(20,14,8,.42)", backdropFilter: "blur(4px)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--paper)", borderRadius: "22px 22px 0 0", border: "1px solid var(--line)", borderBottom: "none", padding: "10px 16px calc(16px + env(safe-area-inset-bottom))", maxHeight: "84vh", overflow: "auto" }}>
            <div style={{ width: 38, height: 4.5, borderRadius: 4, background: "var(--line-2)", margin: "2px auto 12px" }} />
            {projects.length > 1 && (
              <div style={{ marginBottom: 12 }}>
                <div className="kicker" style={{ fontSize: 9, marginBottom: 6 }}>Project</div>
                <select className="select" value={project?.id} onChange={(e) => { nav(`/studio/${e.target.value}/${section}`); setMenuOpen(false); }}>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            <div className="kicker" style={{ fontSize: 9, marginBottom: 6 }}>Sections</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6 }}>
              {allowed.map((s) => {
                const active = s.key === section;
                return (
                  <button key={s.key} onClick={() => { nav(`/studio/${project?.id}/${s.key}`); setMenuOpen(false); }}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 4px 10px", borderRadius: 13, border: "1px solid " + (active ? "var(--accent)" : "var(--line)"), background: active ? "var(--accent-soft)" : "var(--card)", cursor: "pointer", minWidth: 0 }}>
                    <span style={{ position: "relative", color: active ? "var(--accent-ink)" : "var(--ink-2)" }}>
                      {Icon[s.icon]({ width: 20 })}
                      {s.key === "feedback" && openFeedback > 0 && <span className="navcount" style={{ position: "absolute", top: -8, right: -14 }}>{openFeedback}</span>}
                      {s.key === "quotes" && owed > 0 && <span className="navcount warn" style={{ position: "absolute", top: -8, right: -14 }}>!</span>}
                    </span>
                    <span style={{ fontSize: 10.5, lineHeight: 1.2, textAlign: "center", color: active ? "var(--accent-ink)" : "var(--ink-2)" }}>{s.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="row between wrap" style={{ gap: 8, marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
              <button className="btn soft sm" onClick={() => { if (project) nav(`/portal/${project.id}`); setMenuOpen(false); }}><Icon.arrowUpRight width={13} />Client portal</button>
              <span className="row" style={{ gap: 6 }}>
                <span className="dim" style={{ fontSize: 11.5 }}>{who}</span>
                <button className="iconbtn" onClick={toggleTheme} style={{ width: 32, height: 32 }} aria-label="Toggle theme">{theme === "light" ? <Icon.moon width={15} /> : <Icon.sun width={15} />}</button>
                <button className="btn ghost sm" onClick={() => { logout(); nav("/login"); }}><Icon.back width={13} />Sign out</button>
              </span>
            </div>
          </div>
        </div>
      )}
      {/* ---------------- sidebar ---------------- */}
      <aside ref={asideRef} style={{ position: "sticky", top: 0, height: "100vh", borderRight: "1px solid var(--line)", background: "var(--paper-2)", display: "flex", flexDirection: "column", padding: "20px 16px" }}>
        <button onClick={() => nav("/studio/projects")} className="row" style={{ gap: 10, background: "none", border: "none", cursor: "pointer", padding: "4px 6px", marginBottom: 22 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={20} /></div>
          <div style={{ textAlign: "left" }}>
            <div className="serif" style={{ fontSize: 17, lineHeight: 1 }}>{STUDIO.name}</div>
            <div className="kicker" style={{ fontSize: 9.5, marginTop: 3 }}>Studio Cockpit</div>
          </div>
        </button>

        {/* global search */}
        <button className="panel row between" onClick={() => window.dispatchEvent(new Event("cmdk-open"))} style={{ width: "100%", padding: "9px 12px", cursor: "pointer", background: "var(--card)", marginBottom: 12 }} title="Search everything (⌘K)">
          <span className="row" style={{ gap: 9, color: "var(--ink-3)" }}><Icon.brief width={14} /><span style={{ fontSize: 13 }}>Search…</span></span>
          <span className="badge" style={{ fontSize: 9 }}>⌘K</span>
        </button>

        {/* project switcher */}
        <div style={{ position: "relative", marginBottom: 14 }}>
          <button className="panel row between" onClick={() => setSwitcher((s) => !s)} style={{ width: "100%", padding: "10px 12px", cursor: "pointer", background: "var(--card)" }}>
            <span className="row" style={{ gap: 10, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: 8, background: `hsl(${project?.cover ?? 20} 45% 55%)`, flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 500, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{project?.name}</span>
                <span className="dim" style={{ fontSize: 11 }}>{project?.code}</span>
              </span>
            </span>
            <Icon.swap width={15} style={{ color: "var(--ink-3)", flexShrink: 0 }} />
          </button>
          {switcher && (
            <div className="card" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 30, padding: 6, boxShadow: "var(--shadow-lg)" }}>
              {projects.map((p) => (
                <button key={p.id} onClick={() => { nav(`/studio/${p.id}/${section}`); setSwitcher(false); }} className="row" style={{ width: "100%", gap: 10, padding: "9px 10px", border: "none", background: p.id === project?.id ? "var(--paper-2)" : "transparent", borderRadius: 9, cursor: "pointer", textAlign: "left" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 8, background: `hsl(${p.cover} 45% 55%)` }} />
                  <span style={{ flex: 1 }}><span style={{ display: "block", fontSize: 13, fontWeight: 500 }}>{p.name}</span><span className="dim" style={{ fontSize: 10.5 }}>{p.type}</span></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* nav — only the sections this role can open */}
        <nav className="col" style={{ gap: 2, flex: 1, overflow: "auto" }}>
          {SECTIONS.filter((s) => { const cap = SECTION_CAP[s.key]; return !cap || canSee(session, cap, roleMatrix); }).map((s) => (
            <NavLink key={s.key} to={`/studio/${project?.id}/${s.key}`} className="navlink">
              {({ isActive }) => (
                <span className="row between" style={{ width: "100%" }}>
                  <span className="row" style={{ gap: 11 }}>{Icon[s.icon]({ width: 18, style: { color: isActive ? "var(--accent-ink)" : "var(--ink-3)" } })}<span>{s.label}</span></span>
                  {s.key === "feedback" && openFeedback > 0 && <span className="navcount">{openFeedback}</span>}
                  {s.key === "quotes" && owed > 0 && <span className="navcount warn">!</span>}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* footer */}
        <div className="col" style={{ gap: 10, marginTop: 12 }}>
          <div className="row" style={{ justifyContent: "flex-end", paddingRight: 4 }}><SyncBadge /></div>
          <button className="panel row between" onClick={() => project && nav(`/portal/${project.id}`)} style={{ padding: "10px 12px", cursor: "pointer", background: "var(--card)", textAlign: "left" }}>
            <span className="row" style={{ gap: 10 }}><Icon.arrowUpRight width={16} style={{ color: "var(--accent-ink)" }} /><span style={{ fontSize: 13 }}>Open client portal</span></span>
          </button>
          <div className="row between">
            <div className="row" style={{ gap: 9 }}><Avatar name={who} hue={20} size={30} /><div><div style={{ fontSize: 12.5, fontWeight: 500 }}>{who}</div><div className="dim" style={{ fontSize: 10.5 }}>{roleLabel}</div></div></div>
            <div className="row" style={{ gap: 6 }}>
              <button className="iconbtn" onClick={toggleTheme} style={{ width: 32, height: 32 }} aria-label="Toggle theme">{theme === "light" ? <Icon.moon width={16} /> : <Icon.sun width={16} />}</button>
              <button className="iconbtn" onClick={() => { logout(); nav("/login"); }} style={{ width: 32, height: 32 }} title="Sign out"><Icon.back width={16} /></button>
            </div>
          </div>
        </div>
      </aside>

      {/* ---------------- main ---------------- */}
      <main className="shell-main" style={{ minWidth: 0, background: "var(--paper)" }}>
        {children}
      </main>
      <CommandPalette />

      <style>{`
        .navlink { display:block; padding:9px 12px; border-radius:11px; color:var(--ink-2); font-size:14px; font-weight:500; transition: background .2s, color .2s; }
        .navlink:hover { background: var(--card); color: var(--ink); }
        .navlink.active { background: var(--card); color: var(--ink); box-shadow: var(--shadow-sm); }
        .navcount { min-width:20px; height:20px; padding:0 6px; border-radius:20px; background:var(--accent); color:#fff; font-size:11px; font-weight:600; display:grid; place-items:center; font-family:var(--font-mono); }
        .navcount.warn { background: var(--warn); }
        .m-bar { display: none; }
        @media (max-width: 860px){
          main{grid-column:1/-1} aside{display:none}
          .m-bar { display: flex; align-items: center; justify-content: space-between; gap: 10px; grid-column: 1 / -1; position: sticky; top: 0; z-index: 40; padding: 10px 14px; padding-top: calc(10px + env(safe-area-inset-top)); background: color-mix(in srgb, var(--paper) 88%, transparent); -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px); border-bottom: 1px solid var(--line); }
          .shell-main .page-wrap { padding: 18px 16px 60px !important; }
          .shell-main h1.serif { font-size: 28px !important; }
          .shell-main [style*="repeat(5,1fr)"], .shell-main [style*="repeat(4,1fr)"], .shell-main [style*="repeat(3,1fr)"] { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
          .shell-main [style*="1.4fr 1fr"], .shell-main [style*="1fr 1fr"], .shell-main [style*="2fr 1fr"], .shell-main [style*="1fr 1.4fr"], .shell-main [style*="1.2fr 1fr"] { grid-template-columns: 1fr !important; }
          .shell-main table { display: block; overflow-x: auto; max-width: 100%; -webkit-overflow-scrolling: touch; }
          .shell-main .card.pad { padding: 16px; }
        }
      `}</style>
    </div>
  );
}

export function Topbar({ title, sub, actions }: { title: string; sub?: string; actions?: ReactNode }) {
  return (
    <div className="row between" style={{ padding: "16px 34px", borderBottom: "1px solid var(--line)", position: "sticky", top: 0, background: "color-mix(in srgb, var(--paper) 82%, transparent)", backdropFilter: "blur(12px)", zIndex: 20 }}>
      <div><div className="kicker">{sub}</div><div className="serif" style={{ fontSize: 19 }}>{title}</div></div>
      {actions}
    </div>
  );
}
